import React, { useState, useEffect } from 'react';
import { PitchConfig, PitchMatch } from '../../data/pitchData';
import '../../styles/components/bracket-view.scss';

interface PitchBracketSVGProps {
  pitch: PitchConfig;
}

const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

const ConnectorLine = ({ start, end }: { start: { x: number; y: number }; end: { x: number; y: number } }) => {
  const controlPoint = { x: start.x + (end.x - start.x) / 2, y: start.y };
  const path = `M ${start.x} ${start.y} C ${controlPoint.x} ${controlPoint.y}, ${controlPoint.x} ${end.y}, ${end.x} ${end.y}`;
  return <path d={path} stroke="#4294d0" strokeWidth={2} fill="none" />;
};

function PitchMatchBox({ match, pitch }: { match: PitchMatch; pitch: PitchConfig }) {
  const pos = pitch.bracketPositions.find((p) => p.id === match.id);
  if (!pos) return null;

  return (
    <div
      className="match-box"
      style={{ position: 'absolute', top: pos.top, left: pos.left }}
    >
      <div className="match-header">
        <span className="match-number">{match.label}</span>
        <span className="match-time">{match.time} {match.day.substring(0, 3)}</span>
        <span className="match-type">{match.bo}</span>
      </div>
      <div className="match-teams">
        <div className="team">
          <img src={teamPlaceholderImage} alt={match.team1} className="team-logo" />
          <span className="team-name">{match.team1}</span>
        </div>
        <div className="team">
          <img src={teamPlaceholderImage} alt={match.team2} className="team-logo" />
          <span className="team-name">{match.team2}</span>
        </div>
      </div>
    </div>
  );
}

function PitchMobileCard({ match }: { match: PitchMatch }) {
  return (
    <div className="mobile-match-card">
      <div className="card-header">
        <span className="match-number">{match.label}</span>
        <span className="match-time">{match.time} {match.day.substring(0, 3)}</span>
        <span className="match-type">{match.bo}</span>
      </div>
      <div className="teams-container">
        <div className="team-row">
          <img src={teamPlaceholderImage} alt="" className="team-logo" />
          <span className="team-name">{match.team1}</span>
        </div>
        <div className="team-row">
          <img src={teamPlaceholderImage} alt="" className="team-logo" />
          <span className="team-name">{match.team2}</span>
        </div>
      </div>
    </div>
  );
}

function PitchMobileBracket({ pitch }: { pitch: PitchConfig }) {
  const matchMap = new Map(pitch.matches.map((m) => [m.id, m]));

  return (
    <div className="mobile-bracket-view">
      {pitch.mobileSections.map((section) => (
        <div key={section.title} className={`mobile-section ${section.sectionClass}`}>
          <div className="section-title">{section.title}</div>
          {section.rounds.map((round) => (
            <div key={round.label || 'final'} className="round-group">
              {round.label && <div className="round-label">{round.label}</div>}
              <div className="round-matches">
                {round.ids.map((id) => {
                  const match = matchMap.get(id);
                  return match ? <PitchMobileCard key={id} match={match} /> : null;
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const PitchBracketSVG: React.FC<PitchBracketSVGProps> = ({ pitch }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const bracketWidth = Math.min(1120, windowWidth - 40);
  const bracketHeight = pitch.bracketHeight;
  const scaleFactor = windowWidth < 1140 ? (windowWidth - 40) / 1100 : 1;

  // Only render matches that have bracket positions
  const bracketMatches = pitch.matches.filter((m) =>
    pitch.bracketPositions.some((p) => p.id === m.id)
  );

  if (isMobile) {
    return (
      <div className="custom-bracket-container mobile">
        <PitchMobileBracket pitch={pitch} />
      </div>
    );
  }

  return (
    <div className="custom-bracket-container" style={{ overflowX: 'hidden' }}>
      <div
        className="bracket-wrapper"
        style={{
          width: bracketWidth,
          height: bracketHeight,
          position: 'relative',
          transform: scaleFactor < 1 ? `scale(${scaleFactor})` : 'none',
          transformOrigin: 'top left',
        }}
      >
        {/* Section titles (Upper / Lower) */}
        {pitch.showUpperLowerLabels && (
          <>
            <div className="bracket-section-title upper" style={{ top: -20, left: 0, width: '100%' }}>
              UPPER BRACKET
            </div>
            {pitch.bracketSectionDividerY && (
              <>
                <div className="bracket-divider" style={{ top: pitch.bracketSectionDividerY, left: 0, width: '100%' }} />
                <div className="bracket-section-title lower" style={{ top: pitch.bracketSectionDividerY + 10, left: 0, width: '100%' }}>
                  LOWER BRACKET
                </div>
              </>
            )}
          </>
        )}

        {/* Round headers */}
        {pitch.bracketRoundHeaders.map((header, i) => (
          <div
            key={i}
            className="round-header"
            style={{ position: 'absolute', top: header.top, left: header.left }}
          >
            {header.title}
          </div>
        ))}

        {/* Bye labels */}
        {pitch.bracketByeLabels?.map((bye, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: bye.top,
              left: bye.left,
              color: '#4a6a8a',
              fontSize: '11px',
              fontWeight: 500,
              textAlign: 'right',
              width: 45,
            }}
          >
            {bye.title}
          </div>
        ))}

        {/* SVG connector lines */}
        <svg
          width={bracketWidth}
          height={bracketHeight}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
        >
          {pitch.bracketConnectors.map((line, i) => (
            <ConnectorLine key={`conn-${i}`} start={line.start} end={line.end} />
          ))}
        </svg>

        {/* Match boxes */}
        {bracketMatches.map((match) => (
          <PitchMatchBox key={match.id} match={match} pitch={pitch} />
        ))}
      </div>
    </div>
  );
};

export default PitchBracketSVG;
